import { Direction } from "types/battle-engine/battle";
import { RaidId } from "game/data/raids";

export type IntroActionType =
  | "SPAWN"
  | "ANIMATION"
  | "MOVE"
  | "TURN"
  | "WAIT"
  | "SIMULTANEOUS"
  | "TEXT"
  | "CAMERA_MOVE"
  | "CAMERA_ZOOM"
  | "FADE_IN"
  | "FADE_OUT";

export interface IntroActorConfig {
  id: string; // Unique ID for the actor in this scene
  basePokemonName: string;
  isShiny?: boolean;
  scale?: number;
  initialPosition?: { x: number; y: number; direction?: Direction }; // Spawn immediately at start
}

export interface IntroAction {
  type: IntroActionType;

  // SPAWN
  actorId?: string;
  position?: { x: number; y: number }; // Grid coordinates
  direction?: Direction;

  // ANIMATION
  animationName?: string;
  loop?: boolean;

  // MOVE
  destination?: { x: number; y: number };
  duration?: number; // ms

  // TURN
  // direction is used

  // CAMERA_MOVE
  cameraStart?: { y: number }; // Viewport Y (pixels or grid?) - usually grid
  cameraEnd?: { y: number };

  // CAMERA_ZOOM
  zoomStart?: number;
  zoomEnd?: number;

  // TEXT
  title?: string;
  subtitle?: string;

  // WAIT
  waitMs?: number;

  // SIMULTANEOUS
  actions?: IntroAction[];
}

export interface RaidIntroConfig {
  mapId: string; // Background map image ID
  title?: string | undefined; // Raid title to display
  actors: IntroActorConfig[]; // Pre-declare actors to load assets
  initialCameraY?: number | undefined; // Starting grid Y for camera center
  initialZoom?: number | undefined; // Starting zoom level
  sequence: IntroAction[];
}

const generateDefaultIntroConfig = ({
  mapId,
  title,
  boss,
  minions,
}: {
  mapId: string;
  title?: string;
  boss: {
    name: string;
    scale: number;
    initialPosition?: { x: number; y: number; direction?: Direction };
  };
  minions: {
    name: string;
    scale: number;
    initialPosition?: { x: number; y: number; direction?: Direction };
  }[];
}): RaidIntroConfig => {
  return {
    mapId,
    title,
    initialCameraY: 6, // Focused on boss area
    initialZoom: 1.25,
    actors: [
      {
        id: "boss",
        basePokemonName: boss.name,
        scale: boss.scale,
        initialPosition: { x: 3, y: 5, direction: Direction.S },
      },
      // minions[0]
      //   ? {
      //       id: "minion1",
      //       basePokemonName: minions[0].name,
      //       scale: minions[0].scale,
      //       initialPosition: minions[0].initialPosition || {
      //         x: 4,
      //         y: 6,
      //         direction: Direction.SE,
      //       },
      //     }
      //   : null,
      // minions[1]
      //   ? {
      //       id: "minion2",
      //       basePokemonName: minions[1].name,
      //       scale: minions[1].scale,
      //       initialPosition: minions[1].initialPosition || {
      //         x: 8,
      //         y: 6,
      //         direction: Direction.SW,
      //       },
      //     }
      //   : null,
    ].filter(Boolean) as IntroActorConfig[],
    sequence: [
      { type: "FADE_IN", duration: 1000 },
      {
        type: "SIMULTANEOUS",
        actions: [
          {
            type: "ANIMATION",
            actorId: "boss",
            animationName: "Roar",
            loop: false,
          },
          {
            type: "TEXT",
            duration: 2500,
          },
        ],
      },
      { type: "WAIT", waitMs: 500 },
      { type: "FADE_OUT", duration: 800 },
    ],
  };
};

export const RAID_INTRO_CONFIGS: Record<RaidId | "default", RaidIntroConfig> = {
  // Default fallback
  default: {
    mapId: "forest/forest (16)",
    title: "Test Raid",
    initialCameraY: 6,
    initialZoom: 1.25,
    actors: [
      {
        id: "boss",
        basePokemonName: "tangrowth",
        scale: 1.5,
        initialPosition: { x: 6, y: 5, direction: Direction.S },
      },
      {
        id: "minion1",
        basePokemonName: "tangela",
        scale: 1,
        initialPosition: { x: 4, y: 6, direction: Direction.SE },
      },
      {
        id: "minion2",
        basePokemonName: "tangela",
        scale: 1,
        initialPosition: { x: 8, y: 6, direction: Direction.SW },
      },
    ],
    sequence: [
      { type: "FADE_IN", duration: 1000 },
      {
        type: "SIMULTANEOUS",
        actions: [
          {
            type: "ANIMATION",
            actorId: "boss",
            animationName: "Roar",
            loop: false,
          },
          {
            type: "TEXT",
            duration: 2500,
          },
        ],
      },
      { type: "WAIT", waitMs: 500 },
      { type: "FADE_OUT", duration: 800 },
    ],
  },

  // Fire Raid - Charizard Mega X, Camerupt Mega
  [RaidId.GRAND_CINDER_CAVERNS]: generateDefaultIntroConfig({
    title: "Grand Cinder Caverns",
    boss: { name: "charizard-mega-x", scale: 1 },
    mapId: "grand-cinder-caverns/fire (7)",
    minions: [
      { name: "charmander", scale: 1 },
      { name: "numel", scale: 1 },
    ],
  }),

  // Poison Raid - Sneasler, Raticate Alola, Muk Alola, Weezing Galar
  [RaidId.NORTH_MIRIEN_SEWERS]: generateDefaultIntroConfig({
    title: "North Mirien Sewers",
    boss: { name: "weezing-galar", scale: 1 },
    mapId: "north-mirien-sewers/poison (18)",
    minions: [
      { name: "sneasel-hisui", scale: 1 },
      { name: "grimer-alola", scale: 1 },
    ],
  }),

  // Grass/Electric Raid - Electrode Hisui, Raichu Alola, Manectric Mega
  [RaidId.AVALIS_SOUTHERN_PLAINS]: generateDefaultIntroConfig({
    title: "Avalis Southern Plains",
    boss: { name: "manectric-mega", scale: 1 },
    mapId: "avalis-plains/forest (2)",
    minions: [
      { name: "electrike", scale: 1 },
      { name: "voltorb-hisui", scale: 1 },
    ],
  }),

  // Ice Raid - Glalie Mega, Sandslash Alola
  [RaidId.INVERGROUND_CAVES]: generateDefaultIntroConfig({
    title: "Inverground Caves",
    boss: { name: "glalie-mega", scale: 1 },
    mapId: "inverground-caves/ice (1)",
    minions: [
      { name: "snorunt", scale: 1 },
      { name: "snorunt", scale: 1 },
    ],
  }),

  // Steel/Ground Raid - Steelix Mega, Dugtrio Alola, Golem Alola, Clodsire
  [RaidId.NORTHERN_CAELDRIN_FACTORIES]: generateDefaultIntroConfig({
    title: "Northern Caeldrin Factories",
    boss: { name: "steelix-mega", scale: 1.5 },
    mapId: "north-caeldrin-factories/steel (1)",
    minions: [
      { name: "onix", scale: 1.2 },
      { name: "geodude-alola", scale: 1 },
    ],
  }),

  // Dark/Ghost Raid - Absol Mega, Persian Alola, Obstagoon, Cursola
  [RaidId.MIRIEN_SECRET_MANOR]: generateDefaultIntroConfig({
    title: "Mirien Secret Manor",
    boss: { name: "absol-mega", scale: 1 },
    mapId: "mirien-secret-manor/dark (1)",
    minions: [
      { name: "absol", scale: 1 },
      { name: "meowth-alola", scale: 1 },
    ],
  }),

  // Rock Raid - Tyranitar Mega, Arcanine Hisui
  [RaidId.ETRIOS_BARRIERS]: generateDefaultIntroConfig({
    title: "Etrios Barriers",
    boss: { name: "tyranitar-mega", scale: 1 },
    mapId: "etrios-barrier-north/rock (1)",
    minions: [
      { name: "larvitar", scale: 1 },
      { name: "growlithe-hisui", scale: 1 },
    ],
  }),

  // Ice Raid - Ninetales Alola, Mr Rime
  [RaidId.CAMPS_OF_INVERGROUND]: generateDefaultIntroConfig({
    title: "Camps of Inverground",
    boss: { name: "ninetales-alola", scale: 1 },
    mapId: "forgotten-camps-of-inverground/ice (5)",
    minions: [
      { name: "vulpix-alola", scale: 1 },
      { name: "mime-jr", scale: 1 },
    ],
  }),

  // Fire Raid - Houndoom Mega, Typhlosion Hisui
  [RaidId.CINDERFIELDS_HILL]: generateDefaultIntroConfig({
    title: "Cinderfields Hill",
    boss: { name: "houndoom-mega", scale: 1 },
    mapId: "cinderfields-hills/fire (6)",
    minions: [
      { name: "houndour", scale: 1 },
      { name: "cyndaquil", scale: 1 },
    ],
  }),

  // Flying Raid - Altaria Mega, Braviary Hisui
  [RaidId.ASTRID_HILLS]: generateDefaultIntroConfig({
    title: "Astrid Hills",
    boss: { name: "altaria-mega", scale: 1 },
    mapId: "astrid-hills/mountain (11)",
    minions: [
      { name: "swablu", scale: 1 },
      { name: "rufflet", scale: 1 },
    ],
  }),

  // Fighting Raid - Gallade Mega, Medicham Mega, Lucario Mega, Sirfetchd
  [RaidId.CAELDRIN_GREAT_DOJO]: generateDefaultIntroConfig({
    title: "Caeldrin Great Dojo",
    boss: { name: "lucario-mega", scale: 1 },
    // TODO: Add map assets for caeldrin-great-dojo
    mapId: "caeldrin-great-dojo/room (1)",
    minions: [
      { name: "riolu", scale: 1 },
      { name: "meditite", scale: 1 },
    ],
  }),

  // Grass Raid - Decidueye Hisui, Lilligant Hisui
  [RaidId.LIZA_PLAINS]: generateDefaultIntroConfig({
    title: "Liza Plains",
    boss: { name: "decidueye-hisui", scale: 1 },
    mapId: "liza-plains/forest (3)",
    minions: [
      { name: "rowlet", scale: 1 },
      { name: "petilil", scale: 1 },
    ],
  }),

  // Normal Raid - Kangaskhan Mega, Lopunny Mega, Tauros Paldea
  [RaidId.ETRIAS_WESTERN_PLAINS]: generateDefaultIntroConfig({
    title: "Etrias Western Plains",
    boss: { name: "kangaskhan-mega", scale: 1 },
    // TODO: Add map assets for etrias-western-plains
    mapId: "etrias-western-plains/room (1)",
    minions: [
      { name: "buneary", scale: 1 },
      { name: "tauros", scale: 1 },
    ],
  }),

  // Rock Raid - Aerodactyl Mega
  [RaidId.ANCIENT_ISLAND]: generateDefaultIntroConfig({
    title: "Ancient Island",
    boss: { name: "aerodactyl-mega", scale: 1 },
    // TODO: Add map assets for ancient-island
    mapId: "ancient-island/room (1)",
    minions: [
      { name: "anorith", scale: 1 },
      { name: "tirtouga", scale: 1 },
    ],
  }),

  // Ghost Raid - Marowak Alola, Gengar Mega, Sableye Mega, Runerigus
  [RaidId.MIRIEN_GRAVEYARDS]: generateDefaultIntroConfig({
    title: "Mirien Graveyards",
    boss: { name: "gengar-mega", scale: 1 },
    mapId: "mirien-graveyards/dark (8)",
    minions: [
      { name: "gastly", scale: 1 },
      { name: "cubone", scale: 1 },
    ],
  }),

  // Water Raid - Samurott Hisui, Slowbro Mega
  [RaidId.AVALIST_COAST_SOUTH]: generateDefaultIntroConfig({
    title: "Avalist Coast South",
    boss: { name: "slowbro-mega", scale: 1 },
    mapId: "avalist-coast-south/water (1)",
    minions: [
      { name: "slowpoke", scale: 1 },
      { name: "oshawott", scale: 1 },
    ],
  }),

  // Poison Raid - Slowking Galar, Overqwil
  [RaidId.SOUTH_MIRIEN_SEWERS]: generateDefaultIntroConfig({
    title: "South Mirien Sewers",
    boss: { name: "overqwil", scale: 1 },
    mapId: "poison/poison (1)",
    minions: [
      { name: "qwilfish-hisui", scale: 1 },
      { name: "slowpoke-galar", scale: 1 },
    ],
  }),

  // Psychic Raid - Zoroark Hisui, Alakazam Mega, Banette Mega
  [RaidId.HAZEL_SECRET_SHRINE]: generateDefaultIntroConfig({
    title: "Hazel Secret Shrine",
    boss: { name: "alakazam-mega", scale: 1 },
    mapId: "hazel-secret-shrine/psychic (1)",
    minions: [
      { name: "abra", scale: 1 },
      { name: "zorua-hisui", scale: 1 },
    ],
  }),

  // Fairy Raid - Gardevoir Mega, Rapidash Galar, Mawile Mega
  [RaidId.ASTRID_WONDER_GARDENS]: generateDefaultIntroConfig({
    title: "Astrid Wonder Gardens",
    boss: { name: "gardevoir-mega", scale: 1.5 },
    mapId: "astrid-wonder-gardens/garden (12)",
    minions: [
      { name: "ralts", scale: 1 },
      { name: "ponyta-galar", scale: 1 },
    ],
  }),

  // Grass Raid - Exeggutor Alola
  [RaidId.GOLMIR_JUNGLE]: generateDefaultIntroConfig({
    title: "Golmir Jungle",
    boss: { name: "exeggutor-alola", scale: 1.5 },
    mapId: "golmir-jungle/forest (1)",
    minions: [
      { name: "exeggcute", scale: 1 },
      { name: "exeggcute", scale: 1 },
    ],
  }),
} as const;

export const getRaidIntroConfig = (raidName: RaidId): RaidIntroConfig => {
  return RAID_INTRO_CONFIGS[raidName] || RAID_INTRO_CONFIGS["default"];
};
